import { Heading, Hidden, Section, Stack } from "@origin-digital/ods-core";
import React, { ReactElement } from "react";

import {
  ComponentBaseProps,
  HeadingComponentVariants,
} from "@origin-digital/ods-types";
import styled from "styled-components";
import { isNative } from "@origin-digital/platform-helpers";
import { ActionBarMobile, getMobileNavButton } from "./ActionBarMobile";
import { Box } from "./Box";
import { ChatButton } from "./ChatButton";
import { HelpSupportButton } from "./HelpSupportButton";
import { NavTitle } from "./NavTitle";

export interface Premise {
  premiseName: string;
  premiseId: string;
}

export type MobileNavButtonIconType = "back" | "close";
export type MobileNavButtonType = "none" | MobileNavButtonIconType;

export interface ActionBarProps extends ComponentBaseProps {
  title?: string;
  headingComponent?: HeadingComponentVariants;
  mobileNavButton?: MobileNavButtonType;
  onClickMobileNavButton?: (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void | boolean;
  desktopVariant?: "default" | "slim";
  propertySelector?: ReactElement;
  showPropertySelector?: boolean;
  showAllProperties?: boolean;
  showServiceSelector?: boolean;
  serviceSelector?: ReactElement;
  onClickChatButton?: () => void;
  onClickHelpSupportButton?: () => void;
  secondaryColor?: boolean;
  showChatButton?: boolean;
}

const ViewContainer = styled(Box)`
  align-items: center;
  display: flex;
  height: 44px;

  ${({ theme }) => theme.breakpoints.up("lg")} {
    height: 64px;
  }
`;

const StyledHidden = styled(Hidden)`
  width: 100%;
`;

const OuterBox = styled(Box)`
  overflow: hidden;
  position: relative;
  z-index: 1000;
`;

const TitleContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  min-width: 0;
  overflow: hidden;

  && h1,
  && h2,
  && h3,
  && h4,
  && h5,
  && h6 {
    font-size: ${({ theme }) => `${theme.typography.body1.fontSize}`};
    overflow: hidden;
    text-align: center;
    width: 100%;
  }
`;

const PropertySelectorContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex: 0 0 300px;
  justify-content: flex-start;
  max-width: 300px;
  min-width: 0;
  overflow: hidden;
  padding-left: ${({ theme }) => `${theme.space.medium}px`};

  > * {
    max-width: 100%;
    min-width: 0;
  }
`;

const ActionButtonsContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex: 0 0 300px;
  gap: ${({ theme }) => `${theme.space.medium}px`};
  justify-content: flex-end;
  padding-right: ${({ theme }) => `${theme.space.medium}px`};
`;

const StyledBox = styled(Box)<{ $secondaryColor?: boolean }>`
  background-color: ${({ theme, $secondaryColor }) =>
    $secondaryColor
      ? theme.palette.background.secondary
      : theme.palette.backgroundColor.primary};
`;

export const ActionBar: React.FC<ActionBarProps> = ({
  title,
  headingComponent = "h1",
  mobileNavButton = "none",
  onClickMobileNavButton,
  "data-id": dataId = "action-bar",
  desktopVariant = "default",
  // @ts-ignore currently unused, for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showAllProperties = true,
  showPropertySelector = false,
  propertySelector,
  showServiceSelector = false,
  serviceSelector,
  onClickChatButton,
  onClickHelpSupportButton,
  secondaryColor = false,
  showChatButton = false,
}) => (
  <OuterBox>
    <StyledBox
      data-id={dataId}
      display="flex"
      flexDirection="column"
      $secondaryColor={secondaryColor}
    >
      <Box display="flex" width="full">
        <ViewContainer display="flex" width="full">
          <StyledHidden below="lg">
            <Section
              paddingY="none"
              backgroundColor="transparent"
              hideGutter={true}
            >
              <Box display="flex" justifyContent="spaceBetween">
                {desktopVariant === "slim" ? (
                  <Stack>
                    <NavTitle
                      mobileNavButton={mobileNavButton}
                      headingComponent={headingComponent}
                      onClickMobileNavButton={onClickMobileNavButton}
                      title={title}
                      desktopVariant={desktopVariant}
                      isDesktop={true}
                      showPropertySelector={showPropertySelector}
                      propertySelector={propertySelector}
                      onClickChatButton={onClickChatButton}
                      onClickHelpSupportButton={onClickHelpSupportButton}
                      secondaryColor={secondaryColor}
                      showChatButton={showChatButton}
                    />
                  </Stack>
                ) : (
                  <>
                    {isNative() ? (
                      <Box
                        data-id="actionbar-desktop-native-navigation"
                        paddingLeft="xxsmall"
                      >
                        {getMobileNavButton(
                          mobileNavButton,
                          onClickMobileNavButton,
                          true,
                          desktopVariant,
                          showPropertySelector
                        )}
                      </Box>
                    ) : null}
                    <PropertySelectorContainer data-id="actionbar-desktop-property-selector">
                      {showPropertySelector && propertySelector}
                    </PropertySelectorContainer>
                    <TitleContainer>
                      {title ? (
                        <Heading
                          variant="h3"
                          data-id="actionbar-heading-desktop"
                          component={headingComponent}
                          truncate
                        >
                          {title}
                        </Heading>
                      ) : null}
                    </TitleContainer>
                    <ActionButtonsContainer data-id="actionbar-desktop-standard-actions">
                      {onClickHelpSupportButton && (
                        <HelpSupportButton
                          onClick={onClickHelpSupportButton}
                          presentation="desktop"
                          secondaryColor={secondaryColor}
                        />
                      )}
                      {showChatButton && onClickChatButton ? (
                        <ChatButton
                          onClick={onClickChatButton}
                          presentation="desktop"
                          secondaryColor={secondaryColor}
                        />
                      ) : null}
                    </ActionButtonsContainer>
                  </>
                )}
              </Box>
            </Section>
          </StyledHidden>
          <StyledHidden above="lg">
            <ActionBarMobile
              title={title}
              headingComponent={headingComponent}
              mobileNavButton={mobileNavButton}
              onClickMobileNavButton={onClickMobileNavButton}
              showPropertySelector={showPropertySelector}
              propertySelector={propertySelector}
              onClickChatButton={onClickChatButton}
              onClickHelpSupportButton={onClickHelpSupportButton}
              secondaryColor={secondaryColor}
              showChatButton={showChatButton}
            />
          </StyledHidden>
        </ViewContainer>
      </Box>
      {showPropertySelector && showServiceSelector && serviceSelector}
    </StyledBox>
  </OuterBox>
);

ActionBar.displayName = "ActionBar";
